// TEMPORARY MOCK ADAPTER for the R-value (위험지수) threshold card. PRD 09 §3.4/§4.4.
// In-memory only — state lives in this module's `store` and resets on hard reload.
// Mutations resolve with ~300 ms simulated latency. Errors mimic the backend envelope.
// Backend switch: VITE_USE_MOCK_API=false flips to the real HTTP adapter. See risk-range.adapter.ts.

import {
  RiskRangeApiError,
  type RiskRange,
  type RiskRangeApi,
} from '../types/risk-range.types';
import {
  DEFAULT_RISK_RANGE,
  RISK_RANGE_ABS_MIN,
} from '../utils/risk-range.defaults';
import { isNextStep } from '../utils/risk-range.schema';

const LATENCY_MS = 300;

const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

// Server-side guard mirroring PRD 09 §3.4 D-D2: each interval min < max, adjacent continuity
// (the next interval starts one 0.1 step above the previous upper limit — SHCT-681, same reading as
// BiometricService.adjacent), all values >= 0.0 (no upper bound — ADM.024).
const validateRange = (r: RiskRange): void => {
  const belowMin = [
    r.low.min,
    r.low.max,
    r.mid.min,
    r.mid.max,
    r.high.min,
    r.high.max,
  ].some((v) => !Number.isFinite(v) || v < RISK_RANGE_ABS_MIN);
  if (belowMin) {
    throw new RiskRangeApiError('UNKNOWN', '유효하지 않은 값이 있습니다.');
  }

  const continuous =
    r.low.min < r.low.max &&
    r.mid.min < r.mid.max &&
    r.high.min < r.high.max &&
    isNextStep(r.low.max, r.mid.min) &&
    isNextStep(r.mid.max, r.high.min);
  if (!continuous) {
    throw new RiskRangeApiError(
      'RISK_BAND_NOT_CONTINUOUS',
      '구간이 연속적이지 않습니다. 각 구간 하한 < 상한, 다음 구간 하한은 이전 구간 상한 + 0.1이어야 합니다.',
    );
  }
};

// In-memory store seeded from defaults.
let store: RiskRange = clone(DEFAULT_RISK_RANGE);

export const riskRangeMockApi: RiskRangeApi = {
  async getRange(): Promise<RiskRange> {
    await delay();
    return clone(store);
  },

  async updateRange(range: RiskRange): Promise<RiskRange> {
    await delay();
    validateRange(range);
    store = clone(range);
    return clone(store);
  },
};
